import React, { useState } from "react";
import { Users, UserCheck, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, faceImageUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";

const ROLES = ["Bride", "Groom", "Bride Father", "Bride Mother", "Groom Father",
  "Groom Mother", "Sibling", "Family", "Friend", "Officiant", "Guest", "Other"];

export default function People() {
  const { projectId } = useApp();
  const [clustering, setClustering] = useState(false);
  const { data, refresh } = usePoll(
    () => (projectId ? api.persons(projectId) : Promise.resolve(null)), 3000, [projectId]
  );

  if (!projectId) return <NoProject title="People needs a project" />;
  const persons = data?.persons || [];

  const runCluster = async () => {
    setClustering(true);
    try { await api.clusterFaces(projectId); toast.success("Face clustering queued"); }
    catch (e) { toast.error("Failed"); }
    finally { setTimeout(() => { setClustering(false); refresh(); }, 2500); }
  };

  const setLabel = async (p, label) => {
    try { await api.updatePerson(p.id, { label }); refresh(); }
    catch (e) { toast.error("Update failed"); }
  };
  const setRole = async (p, role) => {
    try { await api.updatePerson(p.id, { role, label: role === "Other" ? p.label : role }); toast.success(`Labelled ${role}`); refresh(); }
    catch (e) { toast.error("Update failed"); }
  };

  return (
    <div className="p-8 fade-up space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">People &amp; Faces</h1>
          <p className="text-[#94A3B8] text-sm mt-1">Heuristic appearance clustering — label people, then search footage by identity</p>
        </div>
        <button data-testid="btn-cluster-faces" className="btn btn-gold" onClick={runCluster} disabled={clustering}>
          {clustering ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Re-cluster Faces
        </button>
      </div>

      <div className="studio-card p-4 flex items-center gap-6 text-sm">
        <span className="flex items-center gap-2"><Users size={16} className="text-[#E6B04E]" /> {persons.length} person cluster(s)</span>
        <span className="text-[#94A3B8]">Faces detected depend on running <b>Wedding AI</b> on the Intelligence page first.</span>
      </div>

      {persons.length === 0 ? (
        <div className="studio-card p-12 text-center text-[#64748B]">
          No person clusters yet. Run <b>Wedding AI</b> (face detection) on the AI Analysis page, then re-cluster.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {persons.map((p) => (
            <div key={p.id} data-testid={`person-card-${p.id}`} className="studio-card p-4">
              <div className="aspect-square rounded-xl bg-[#0c0e14] overflow-hidden mb-3 flex items-center justify-center">
                {p.representative_face ? (
                  <img src={faceImageUrl(p.representative_face)} alt={p.label} className="w-full h-full object-cover" />
                ) : <Users size={30} className="text-[#334155]" />}
              </div>
              <input
                data-testid={`person-label-${p.id}`}
                className="studio-input !py-1.5 text-sm mb-2"
                defaultValue={p.label}
                onBlur={(e) => e.target.value !== p.label && setLabel(p, e.target.value)}
              />
              <select data-testid={`person-role-${p.id}`} className="studio-input !py-1.5 text-sm mb-2"
                value={p.role || ""} onChange={(e) => setRole(p, e.target.value)}>
                <option value="">Set role…</option>
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </select>
              <div className="flex items-center justify-between metric-label" style={{ fontSize: 9 }}>
                <span><UserCheck size={11} className="inline mr-1" />{p.face_count} faces</span>
                <span>{(p.media_ids || []).length} clips</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
